import { useEffect, useMemo, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { type WorkspaceLayoutPreset } from 'generated/graphql';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { type WithApolloContext } from 'modules/Apollo/types';
import { extensionAppState } from 'hooks/useExtensionState';
import { activeWorkspaceLayoutPresetState, workspaceDisplayArrangementState } from './state';
import { CompatibilityResult, getWorkspaceLayoutPresetCompatibility } from './utils';
import { useWorkspaceLayoutManager } from './useWorkspaceLayoutManager';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

type LayoutPresetForApply = Pick<
  WorkspaceLayoutPreset,
  'id' | 'name' | 'windows' | 'displayArrangementKey' | 'displaySlots' | 'isDefault' | 'updatedAt'
>;

const byMostRecent = (a: LayoutPresetForApply, b: LayoutPresetForApply): number =>
  new Date(String(b.updatedAt)).getTime() - new Date(String(a.updatedAt)).getTime();

const getWindowsSignature = (
  windows: typeof extensionAppState extends never ? never : Record<string, any>
) =>
  Object.entries(windows)
    .filter(([, tab]) => tab != null)
    .map(([name, tab]) => `${name}:${tab.id}:${tab.path}`)
    .sort()
    .join('|');

export function useAutoApplyWorkspaceLayoutPreset({ context }: WithApolloContext): void {
  const [isWorkspaceLayoutV2Enabled] = useFeatureFlagEnabled(FF.WORKSPACE_LAYOUT_V2);

  const { data } = useCurrentUser({ context });
  const extensionState = useRecoilValue(extensionAppState);
  const displayArrangement = useRecoilValue(workspaceDisplayArrangementState);
  const activeLayoutPreset = useRecoilValue(activeWorkspaceLayoutPresetState);
  const setActiveLayoutPreset = useSetRecoilState(activeWorkspaceLayoutPresetState);
  const { applyWorkspaceLayoutPreset } = useWorkspaceLayoutManager();
  const lastApplyRef = useRef<{ presetId: string; windowsSignature: string } | null>(null);

  const currentDisplays = useMemo(() => displayArrangement?.displays ?? [], [displayArrangement]);
  const presets = useMemo<LayoutPresetForApply[]>(
    () => [...(data?.me?.workspaceSettings?.layoutSettings?.presets ?? [])],
    [data?.me?.workspaceSettings?.layoutSettings?.presets]
  );

  const exactDefaultPreset = useMemo(() => {
    return presets
      .filter(
        (preset) =>
          preset.isDefault &&
          getWorkspaceLayoutPresetCompatibility([...preset.displaySlots], currentDisplays) ===
            CompatibilityResult.Exact
      )
      .sort(byMostRecent)[0];
  }, [currentDisplays, presets]);

  const activePreset = useMemo(
    () => presets.find((preset) => preset.id === activeLayoutPreset?.presetId),
    [activeLayoutPreset?.presetId, presets]
  );

  const activePresetCompatibility = useMemo(() => {
    if (activePreset == null) return CompatibilityResult.Incompatible;

    return getWorkspaceLayoutPresetCompatibility([...activePreset.displaySlots], currentDisplays);
  }, [activePreset, currentDisplays]);

  const presetToApply =
    activePreset != null && activePresetCompatibility !== CompatibilityResult.Incompatible
      ? activePreset
      : exactDefaultPreset;

  const windowsSignature = useMemo(
    () => getWindowsSignature(extensionState.windows as Record<string, any>),
    [extensionState.windows]
  );

  useEffect(() => {
    if (activeLayoutPreset == null || !isWorkspaceLayoutV2Enabled) {
      return;
    }

    if (activePreset == null || activePresetCompatibility === CompatibilityResult.Incompatible) {
      setActiveLayoutPreset(null);
    }
  }, [
    activeLayoutPreset,
    activePreset,
    activePresetCompatibility,
    isWorkspaceLayoutV2Enabled,
    setActiveLayoutPreset,
  ]);

  useEffect(() => {
    if (
      !isWorkspaceLayoutV2Enabled ||
      presetToApply == null ||
      windowsSignature.length === 0 ||
      presetToApply.windows.length === 0
    ) {
      return;
    }

    const shouldSyncActivePreset =
      activeLayoutPreset?.presetId !== presetToApply.id ||
      activeLayoutPreset?.displayArrangementKey !== presetToApply.displayArrangementKey;
    if (shouldSyncActivePreset) {
      setActiveLayoutPreset({
        presetId: presetToApply.id,
        displayArrangementKey: presetToApply.displayArrangementKey,
      });
    }

    const alreadyApplied =
      lastApplyRef.current?.presetId === presetToApply.id &&
      lastApplyRef.current?.windowsSignature === windowsSignature;
    if (alreadyApplied) return;

    applyWorkspaceLayoutPreset(presetToApply);
    lastApplyRef.current = {
      presetId: presetToApply.id,
      windowsSignature,
    };
  }, [
    activeLayoutPreset?.displayArrangementKey,
    activeLayoutPreset?.presetId,
    applyWorkspaceLayoutPreset,
    isWorkspaceLayoutV2Enabled,
    presetToApply,
    setActiveLayoutPreset,
    windowsSignature,
  ]);
}
